/* eslint-disable react-hooks/exhaustive-deps */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import Chart from "react-apexcharts";
import type { ApexOptions } from "apexcharts";
import { useDashboardStats } from "../../store/useDashboardStats";
import { PrimaryButton } from "../buttons/PrimaryButton";
import {
  Menu,
  MenuHandler,
  MenuItem,
  MenuList,
  Tooltip,
} from "@material-tailwind/react";
import { ReactComponent as ChevronDown } from "../../assets/icons/chevrons/chevron-down.svg";
import dayjs from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";
import axios from "axios";
// @ts-ignore
import { useScreenshot } from "use-react-screenshot";
import { TOTAL_CONTRAST_COLOR } from "../../constants/plasticColors";
import InformationIcon from "../../assets/icons/info/information.png";

dayjs.extend(isoWeek);

interface Props {
  filterType?: "location" | "city";
  filterPlaces?: string[];
  /**
   * "cumulative" (default) shows running totals,
   * "perPeriod" shows amounts per period (non-cumulative).
   */
  mode?: "cumulative" | "perPeriod";
}

const monthOptions = [
  { value: 1, label: "Jan" },
  { value: 2, label: "Feb" },
  { value: 3, label: "Mar" },
  { value: 4, label: "Apr" },
  { value: 5, label: "May" },
  { value: 6, label: "Jun" },
  { value: 7, label: "Jul" },
  { value: 8, label: "Aug" },
  { value: 9, label: "Sep" },
  { value: 10, label: "Oct" },
  { value: 11, label: "Nov" },
  { value: 12, label: "Dec" },
];

const quarterOptions = [
  { value: 1, label: "Q1" },
  { value: 2, label: "Q2" },
  { value: 3, label: "Q3" },
  { value: 4, label: "Q4" },
];

const WALLET_COLOR = "#23C16B";
const DONATIONS_COLOR = TOTAL_CONTRAST_COLOR;

export const WalletDonationsCumulative = React.memo(
  ({ mode = "cumulative" }: Props) => {
    const snapshotRef = useRef(null);
    const {
      walletAndDonationsCumulative,
      getWalletAndDonationsCumulative,
      isLoadingWalletAndDonations,
      isWalletAndDonationsError,
    } = useDashboardStats();
    const [, takeScreenshot] = useScreenshot();

    const [period, setPeriod] = useState<"month" | "week" | "quarter" | "year">(
      "month"
    );
    const [startYear, setStartYear] = useState(() => {
      const d = dayjs().subtract(12, "month");
      return d.year();
    });
    const [startMonth, setStartMonth] = useState(() => {
      const d = dayjs().subtract(12, "month");
      return d.month() + 1;
    });
    const [startWeek, setStartWeek] = useState(() => {
      const d = dayjs().subtract(12, "month");
      return d.isoWeek();
    });
    const [startQuarter, setStartQuarter] = useState(1);
    const [endYear, setEndYear] = useState(dayjs().year());
    const [endMonth, setEndMonth] = useState(dayjs().month() + 1);
    const [endWeek, setEndWeek] = useState(dayjs().isoWeek());
    const [endQuarter, setEndQuarter] = useState(4);

    const yearOptions = Array.from(
      { length: dayjs().year() - 2023 },
      (_, i) => dayjs().year() - i
    );

    const getWeekOptions = (year: number) => {
      const lastDay = dayjs(`${year}-12-31`);
      const totalWeeks = lastDay.isoWeek() === 1 ? 52 : lastDay.isoWeek();
      const currentWeek = dayjs().isoWeek();
      const currentYear = dayjs().year();
      return Array.from({ length: totalWeeks }, (_, i) => {
        const w = i + 1;
        const start = dayjs().year(year).isoWeek(w).startOf("isoWeek");
        const end = dayjs().year(year).isoWeek(w).endOf("isoWeek");
        return {
          value: w,
          label: `Week ${w}: ${start.format("MMM DD")} - ${end.format("MMM DD")}`,
          disabled: year === currentYear && w > currentWeek,
        };
      });
    };

    const startMonthOptions = useMemo(() => {
      const cy = dayjs().year();
      const cm = dayjs().month() + 1;
      return startYear === cy
        ? monthOptions.filter((m) => m.value <= cm)
        : monthOptions;
    }, [startYear]);

    const endMonthOptions = useMemo(() => {
      const cy = dayjs().year();
      const cm = dayjs().month() + 1;
      return endYear === cy
        ? monthOptions.filter((m) => m.value <= cm)
        : monthOptions;
    }, [endYear]);

    const startWeekOptions = useMemo(
      () => getWeekOptions(startYear),
      [startYear]
    );
    const endWeekOptions = useMemo(() => getWeekOptions(endYear), [endYear]);

    const rawData = walletAndDonationsCumulative ?? [];
    const chartSeries = useMemo(() => {
      if (period !== "year" || !rawData.length) return rawData;
      return rawData.map((s) => {
        const sData =
          (s.data as unknown as [number, number][] | undefined) ?? [];
        const yearSums = new Map<number, number>();
        sData.forEach(([ts, val]) => {
          const y = dayjs(ts).year();
          if (y >= startYear && y <= endYear) {
            yearSums.set(y, (yearSums.get(y) ?? 0) + val);
          }
        });
        const data: [number, number][] = [];
        for (let y = startYear; y <= endYear; y++) {
          data.push([new Date(y, 0, 1).getTime(), yearSums.get(y) ?? 0]);
        }
        return { ...s, data };
      });
    }, [rawData, period, startYear, endYear]);

    const baseSeries = chartSeries ?? [];

    const displaySeries = useMemo(() => {
      if (!baseSeries.length) return [];
      if (mode === "cumulative") return baseSeries;

      // Non-cumulative: convert running totals into per-period amounts
      return baseSeries.map((s) => {
        const data = ((s.data as unknown as [number, number][]) ?? []).map(
          ([ts, value], idx, arr) => {
            if (idx === 0) {
              return [ts, value] as [number, number];
            }
            const prev = arr[idx - 1]?.[1] ?? 0;
            return [ts, value - prev] as [number, number];
          }
        );

        const cleanedName =
          s.name === "Donations cumulative"
            ? "Donations"
            : (s.name || "").replace(/cumulative/gi, "").trim() || s.name;

        return { ...s, name: cleanedName, data };
      });
    }, [baseSeries, mode]);

    const seriesNames = useMemo(
      () => displaySeries.map((s) => s?.name || ""),
      [displaySeries]
    );
    const seriesColors = useMemo(
      () =>
        seriesNames.map((name) =>
          name === "Wallet" ? WALLET_COLOR : DONATIONS_COLOR
        ),
      [seriesNames]
    );

    const xaxisLabelsFormatter = useCallback(
      (value: string | number) => {
        const ts = typeof value === "string" ? parseFloat(value) : value;
        if (period === "year") return dayjs(ts).format("YYYY");
        if (period === "quarter") {
          const d = dayjs(ts);
          const q = Math.ceil((d.month() + 1) / 3);
          return `Q${q} ${d.year()}`;
        }
        if (period === "week") {
          return `${dayjs(ts).year()}-W${String(dayjs(ts).isoWeek()).padStart(2, "0")}`;
        }
        return dayjs(ts).format("MMM YYYY");
      },
      [period]
    );

    const downloadScreenshot = () => {
      takeScreenshot(snapshotRef.current).then((image: any) => {
        const link = document.createElement("a");
        link.href = image;
        link.download =
          mode === "cumulative"
            ? "wallet_donations_cumulative.png"
            : "wallet_donations_per_period.png";
        link.click();
      });
    };

    const downloadCsv = useCallback(() => {
      if (!displaySeries.length) return;
      const buildDailyParams = () => {
        if (period === "week") {
          const startDate = dayjs().year(startYear).isoWeek(startWeek).startOf("isoWeek");
          const endDate = dayjs().year(endYear).isoWeek(endWeek).endOf("isoWeek");
          return {
            params: {
              period: "day",
              fromYear: startDate.year(),
              fromMonth: startDate.month() + 1,
              toYear: endDate.year(),
              toMonth: endDate.month() + 1,
            },
            startTs: startDate.startOf("day").valueOf(),
            endTs: endDate.endOf("day").valueOf(),
          };
        }

        return {
          params: {
            period: "day",
            fromYear: startYear,
            fromMonth:
              period === "year" ? 1 : period === "quarter" ? (startQuarter - 1) * 3 + 1 : startMonth,
            toYear: endYear,
            toMonth:
              period === "year" ? 12 : period === "quarter" ? endQuarter * 3 : endMonth,
          },
        };
      };

      axios
        .get("/withdraws/stats/wallet-and-donations-cumulative", {
          params: buildDailyParams().params,
        })
        .then((response) => {
          const { startTs, endTs } = buildDailyParams();
          const dailySeries =
            ((response.data as typeof displaySeries) ?? []).map((item) => ({
              ...item,
              data:
                ((item.data as unknown as [number, number][]) ?? []).filter(
                  ([ts]) =>
                    (startTs == null || ts >= startTs) &&
                    (endTs == null || ts <= endTs)
                ),
            })) ?? [];

          const headers = ["Date", ...dailySeries.map((s) => s?.name || "")];
          const lines = [headers.join(",")];
          const firstSeries = dailySeries[0];
          const dataArr =
            (firstSeries?.data as unknown as [number, number][] | undefined) ?? [];

          for (let i = 0; i < dataArr.length; i++) {
            const [ts] = dataArr[i];
            const row = [dayjs(ts).format("YYYY-MM-DD")];
            for (const s of dailySeries) {
              const d = (s.data as unknown as [number, number][] | undefined) ?? [];
              const val = d[i]?.[1] ?? 0;
              row.push((val / 100).toFixed(2));
            }
            lines.push(row.join(","));
          }

          const blob = new Blob([lines.join("\n")], {
            type: "text/csv;charset=utf-8;",
          });
          const url = URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.download = "wallet_donations_daily.csv";
          link.click();
          URL.revokeObjectURL(url);
        })
        .catch((error) => {
          console.error("Error downloading wallet/donations CSV:", error);
        });
    }, [displaySeries, endMonth, endQuarter, endWeek, endYear, period, seriesNames, startMonth, startQuarter, startWeek, startYear]);

    useEffect(() => {
      const apiPeriod: "month" | "week" | "quarter" | "year" = period;

      const fromToParams = {
        fromYear: startYear,
        toYear: endYear,
        fromMonth:
          apiPeriod === "month" || apiPeriod === "year"
            ? apiPeriod === "year"
              ? 1
              : startMonth
            : undefined,
        toMonth:
          apiPeriod === "month" || apiPeriod === "year"
            ? apiPeriod === "year"
              ? 12
              : endMonth
            : undefined,
        fromWeek: apiPeriod === "week" ? startWeek : undefined,
        toWeek: apiPeriod === "week" ? endWeek : undefined,
        fromQuarter: apiPeriod === "quarter" ? startQuarter : undefined,
        toQuarter: apiPeriod === "quarter" ? endQuarter : undefined,
      };

      getWalletAndDonationsCumulative(apiPeriod, fromToParams);
    }, [
      period,
      startYear,
      startMonth,
      startWeek,
      startQuarter,
      endYear,
      endMonth,
      endWeek,
      endQuarter,
      getWalletAndDonationsCumulative,
    ]);

    return (
      <div className="flex flex-col w-full p-2 sm:p-7 bg-white rounded-xl shadow-none mb-1 sm:mb-10">
        <div className="flex flex-col sm:flex-row w-full items-start justify-between">
          <p></p>
          <div className="flex flex-col sm:flex-row sm:flex-wrap items-start sm:items-center justify-start sm:justify-end gap-2 sm:gap-3">
            <div className="flex items-center gap-1 sm:gap-2">
              <Menu>
                <MenuHandler>
                  <button className="h-8 px-2 sm:px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-xs sm:text-sm whitespace-nowrap">
                    {period === "month"
                      ? "Monthly"
                      : period === "week"
                        ? "Weekly"
                        : period === "quarter"
                          ? "Quarterly"
                          : "Yearly"}
                    <ChevronDown className="ml-1 sm:ml-2" />
                  </button>
                </MenuHandler>
                <MenuList placeholder={undefined}>
                  {["month", "week", "quarter", "year"].map((p) => (
                    <MenuItem
                      key={p}
                      disabled={period === p}
                      onClick={() => setPeriod(p as typeof period)}
                      className="font-satoshi text-base text-dark"
                      placeholder={undefined}
                    >
                      {p === "month"
                        ? "Monthly"
                        : p === "week"
                          ? "Weekly"
                          : p === "quarter"
                            ? "Quarterly"
                            : "Yearly"}
                    </MenuItem>
                  ))}
                </MenuList>
              </Menu>
            </div>

            <div className="flex items-center gap-1 sm:gap-2">
              <Menu>
                <MenuHandler>
                  <button className="h-8 px-2 sm:px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-xs sm:text-sm">
                    {startYear}
                    <ChevronDown className="ml-1 sm:ml-2" />
                  </button>
                </MenuHandler>
                <MenuList placeholder={undefined}>
                  {yearOptions.map((y) => (
                    <MenuItem
                      key={y}
                      disabled={startYear === y}
                      onClick={() => setStartYear(y)}
                      className="font-satoshi text-base text-dark"
                      placeholder={undefined}
                    >
                      {y}
                    </MenuItem>
                  ))}
                </MenuList>
              </Menu>
              {period === "month" && (
                <Menu>
                  <MenuHandler>
                    <button className="h-8 px-2 sm:px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-xs sm:text-sm">
                      {startMonthOptions.find((m) => m.value === startMonth)
                        ?.label || "Month"}
                      <ChevronDown className="ml-1 sm:ml-2" />
                    </button>
                  </MenuHandler>
                  <MenuList placeholder={undefined}>
                    {startMonthOptions.map((m) => (
                      <MenuItem
                        key={m.value}
                        disabled={startMonth === m.value}
                        onClick={() => setStartMonth(m.value)}
                        className="font-satoshi text-base text-dark"
                        placeholder={undefined}
                      >
                        {m.label}
                      </MenuItem>
                    ))}
                  </MenuList>
                </Menu>
              )}
              {period === "week" && (
                <Menu>
                  <MenuHandler>
                    <button
                      className="h-8 px-2 sm:px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-xs sm:text-sm max-w-[180px] sm:max-w-[220px] whitespace-nowrap overflow-hidden text-ellipsis"
                      title={
                        startWeekOptions.find((w) => w.value === startWeek)
                          ?.label
                      }
                    >
                      {startWeekOptions.find((w) => w.value === startWeek)
                        ?.label || "Week"}
                      <ChevronDown className="ml-1 sm:ml-2 flex-shrink-0" />
                    </button>
                  </MenuHandler>
                  <MenuList
                    className="rounded-none max-h-[250px] overflow-y-auto"
                    placeholder={undefined}
                  >
                    {startWeekOptions.map((w) => (
                      <MenuItem
                        key={w.value}
                        disabled={w.disabled || startWeek === w.value}
                        onClick={() => setStartWeek(w.value)}
                        className="font-satoshi text-base text-dark"
                        placeholder={undefined}
                      >
                        {w.label}
                      </MenuItem>
                    ))}
                  </MenuList>
                </Menu>
              )}
              {period === "quarter" && (
                <Menu>
                  <MenuHandler>
                    <button className="h-8 px-2 sm:px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-xs sm:text-sm">
                      {quarterOptions.find((q) => q.value === startQuarter)
                        ?.label || "Q1"}
                      <ChevronDown className="ml-1 sm:ml-2" />
                    </button>
                  </MenuHandler>
                  <MenuList placeholder={undefined}>
                    {quarterOptions.map((q) => (
                      <MenuItem
                        key={q.value}
                        disabled={startQuarter === q.value}
                        onClick={() => setStartQuarter(q.value)}
                        className="font-satoshi text-base text-dark"
                        placeholder={undefined}
                      >
                        {q.label}
                      </MenuItem>
                    ))}
                  </MenuList>
                </Menu>
              )}
            </div>

            <div className="flex items-center gap-1 sm:gap-2">
              <label className="font-satoshi text-xs sm:text-sm text-gray-text whitespace-nowrap">
                To:
              </label>
              <Menu>
                <MenuHandler>
                  <button className="h-8 px-2 sm:px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-xs sm:text-sm">
                    {endYear}
                    <ChevronDown className="ml-1 sm:ml-2" />
                  </button>
                </MenuHandler>
                <MenuList placeholder={undefined}>
                  {yearOptions.map((y) => (
                    <MenuItem
                      key={y}
                      disabled={endYear === y}
                      onClick={() => setEndYear(y)}
                      className="font-satoshi text-base text-dark"
                      placeholder={undefined}
                    >
                      {y}
                    </MenuItem>
                  ))}
                </MenuList>
              </Menu>
              {period === "month" && (
                <Menu>
                  <MenuHandler>
                    <button className="h-8 px-2 sm:px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-xs sm:text-sm">
                      {endMonthOptions.find((m) => m.value === endMonth)
                        ?.label || "Month"}
                      <ChevronDown className="ml-1 sm:ml-2" />
                    </button>
                  </MenuHandler>
                  <MenuList placeholder={undefined}>
                    {endMonthOptions.map((m) => (
                      <MenuItem
                        key={m.value}
                        disabled={endMonth === m.value}
                        onClick={() => setEndMonth(m.value)}
                        className="font-satoshi text-base text-dark"
                        placeholder={undefined}
                      >
                        {m.label}
                      </MenuItem>
                    ))}
                  </MenuList>
                </Menu>
              )}
              {period === "week" && (
                <Menu>
                  <MenuHandler>
                    <button
                      className="h-8 px-2 sm:px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-xs sm:text-sm max-w-[180px] sm:max-w-[220px] whitespace-nowrap overflow-hidden text-ellipsis"
                      title={
                        endWeekOptions.find((w) => w.value === endWeek)?.label
                      }
                    >
                      {endWeekOptions.find((w) => w.value === endWeek)?.label ||
                        "Week"}
                      <ChevronDown className="ml-1 sm:ml-2 flex-shrink-0" />
                    </button>
                  </MenuHandler>
                  <MenuList
                    className="rounded-none max-h-[250px] overflow-y-auto"
                    placeholder={undefined}
                  >
                    {endWeekOptions.map((w) => (
                      <MenuItem
                        key={w.value}
                        disabled={w.disabled || endWeek === w.value}
                        onClick={() => setEndWeek(w.value)}
                        className="font-satoshi text-base text-dark"
                        placeholder={undefined}
                      >
                        {w.label}
                      </MenuItem>
                    ))}
                  </MenuList>
                </Menu>
              )}
              {period === "quarter" && (
                <Menu>
                  <MenuHandler>
                    <button className="h-8 px-2 sm:px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-xs sm:text-sm">
                      {quarterOptions.find((q) => q.value === endQuarter)
                        ?.label || "Q4"}
                      <ChevronDown className="ml-1 sm:ml-2" />
                    </button>
                  </MenuHandler>
                  <MenuList placeholder={undefined}>
                    {quarterOptions.map((q) => (
                      <MenuItem
                        key={q.value}
                        disabled={endQuarter === q.value}
                        onClick={() => setEndQuarter(q.value)}
                        className="font-satoshi text-base text-dark"
                        placeholder={undefined}
                      >
                        {q.label}
                      </MenuItem>
                    ))}
                  </MenuList>
                </Menu>
              )}
            </div>

            <PrimaryButton
              size="sm"
              onClick={downloadCsv}
              label="Download CSV"
              className="!bg-droppie-oranje !border-droppie-oranje active:!bg-droppie-oranje-dark"
            />
            <PrimaryButton
              size="sm"
              onClick={downloadScreenshot}
              label="Download image"
              className="!bg-droppie-oranje !border-droppie-oranje active:!bg-droppie-oranje-dark"
            />
          </div>
        </div>
        <div ref={snapshotRef} className="mt-4">
          <div className="flex items-center mb-2 sm:mb-12">
            <p className="font-satoshi font-bold text-lg sm:text-2xl text-tailgrids-black">
              Wallet + donations over time (€)
            </p>
            <Tooltip
              content={
                mode === "cumulative"
                  ? "Wallet = payout to customer minus withdrawals (running total). Donations = total amount donated from wallets over time. Values are in euros."
                  : "Wallet = net change in wallet (payouts minus withdrawals) per period. Donations = amount donated from wallets per period. Values are in euros."
              }
              placeholder={undefined}
            >
              <img
                src={InformationIcon}
                alt="info"
                className="inline-block w-4 h-4 ml-2 cursor-pointer"
              />
            </Tooltip>
          </div>
          {isLoadingWalletAndDonations ? (
            <div className="flex flex-col items-center justify-center min-h-[300px] w-full gap-2 bg-gray-50/0 rounded-lg">
              <svg
                aria-hidden="true"
                className="w-8 h-8 text-gray-200 animate-spin fill-primary-base"
                viewBox="0 0 100 101"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                  fill="currentColor"
                />
                <path
                  d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                  fill="currentFill"
                />
              </svg>
            </div>
          ) : isWalletAndDonationsError ? (
            <div className="flex flex-col items-center justify-center min-h-[300px] w-full">
              <p className="text-red-500 font-satoshi">
                Failed to load data. Please try again.
              </p>
            </div>
          ) : displaySeries.length > 0 ? (
            <Chart
              options={
                {
                  ...walletDonationsChartOptions,
                  colors:
                    seriesColors.length > 0
                      ? seriesColors
                      : walletDonationsChartOptions.colors,
                  xaxis: {
                    tickAmount:
                      period === "year"
                        ? Math.max(0, endYear - startYear)
                        : undefined,
                    labels: {
                      formatter: xaxisLabelsFormatter,
                    },
                  },
                  legend: {
                    show: true,
                    position: "right",
                  },
                } as ApexOptions
              }
              series={displaySeries}
              height={300}
            />
          ) : null}
        </div>
      </div>
    );
  }
);

const walletDonationsChartOptions: ApexOptions = {
  chart: {
    type: "line",
    zoom: { enabled: false },
    toolbar: { show: false },
  },
  dataLabels: { enabled: false },
  grid: { strokeDashArray: 5 },
  markers: { size: 1 },
  tooltip: {
    shared: true,
    intersect: false,
    custom: function ({ seriesIndex, dataPointIndex, w }: any) {
      const seriesData = w.config.series.map((s: any, index: number) => ({
        name: s.name,
        value: s.data[dataPointIndex]?.[1] ?? 0,
        color: w.config.colors[index],
      }));
      seriesData.sort((a: any, b: any) => b.value - a.value);
      let tooltipHtml =
        '<div class="apexcharts-tooltip-title" style="font-family: Helvetica, Arial, sans-serif; font-size: 12px;">';
      tooltipHtml += `${dayjs(
        w.globals.seriesX[seriesIndex][dataPointIndex]
      ).format("MMM DD, YYYY")}</div>`;
      seriesData.forEach((item: any) => {
        const formattedValue = new Intl.NumberFormat("nl-NL", {
          style: "currency",
          currency: "EUR",
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(Number(item.value ?? 0) / 100);
        tooltipHtml += `
          <div class="apexcharts-tooltip-series-group apexcharts-active" style="order: 1; display: flex;">
            <span class="apexcharts-tooltip-marker" style="background-color: ${item.color};"></span>
            <div class="apexcharts-tooltip-text" style="font-family: Helvetica, Arial, sans-serif; font-size: 12px;">
              <div class="apexcharts-tooltip-y-group">
                <span class="apexcharts-tooltip-text-y-label">${item.name}: </span>
                <span class="apexcharts-tooltip-text-y-value">${formattedValue}</span>
              </div>
            </div>
          </div>
        `;
      });
      return tooltipHtml;
    },
  },
  yaxis: {
    title: {
      text: "Amount (€)",
      style: { fontSize: "14px", fontWeight: "bold" },
    },
    labels: {
      formatter: function (value: number) {
        return `${Number((value ? value / 100 : 0)?.toFixed(2))}`;
      },
    },
  },
  colors: [WALLET_COLOR, DONATIONS_COLOR],
};
